import React from "react";

import { toLower } from "lodash/fp";
import { DateTime } from "luxon";

import {
  RANGE_PRESETS,
  isMaxDateRangePreset,
} from "@app/analytics/components/DateSelector/utils";
import { isTopicReport } from "@app/analytics/MeetingReports/utils";
import { DateRangePreset, Topic } from "@app/analytics/types";
import { PRODUCTIVITY_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import type { User, UserMeeting } from "@app/common/types";

import type { Report } from "../types";

export const isMenuCloseEvent = (event: React.MouseEvent<HTMLElement>) => {
  if (!event.target) return false;
  return (event.target as HTMLElement).classList.contains("MuiBackdrop-root");
};

export const getFormattedDateRange = (topic: Topic) => {
  const isSingleDate = topic.firstMention
    .startOf("day")
    .equals(topic.lastMention.startOf("day"));

  const isSameYear =
    isSingleDate || topic.firstMention.year === topic.lastMention.year;

  const formattedStartTime = topic.firstMention.toLocaleString({
    month: "short",
    day: "numeric",
    year: isSameYear ? undefined : "numeric",
  });

  if (isSingleDate) return formattedStartTime;

  return `${formattedStartTime} - ${topic.lastMention.toLocaleString({
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
};

const getSuggestedPreset = (
  preset: DateRangePreset | null
): DateRangePreset | undefined => {
  if (!preset || isMaxDateRangePreset(preset)) return;

  const presetNumber = Number(preset);

  switch (true) {
    case presetNumber >= 180:
      return "365";
    case presetNumber >= 90:
      return "180";
    case presetNumber >= 30:
      return "90";
    default:
      return "30";
  }
};

export const getEmptyStateOpts = (opts: {
  selectedPreset: DateRangePreset | null;
  hasAppliedFilters: boolean;
}) => {
  if (opts.hasAppliedFilters) {
    return {
      title: "No Reports",
      subtitle: "Remove filters to see your reports.",
    };
  }

  if (opts.selectedPreset) {
    const title = `No Reports ${
      opts.selectedPreset === "0"
        ? "for today"
        : `in the ${
            opts.selectedPreset
              ? toLower(RANGE_PRESETS[opts.selectedPreset])
              : "selected range"
          }`
    }`;

    if (isMaxDateRangePreset(opts.selectedPreset)) {
      return {
        title,
        subtitle: "Add Read to an upcoming meeting to start seeing reports.",
      };
    }

    return {
      title,
      subtitle:
        "Try expanding the date range, or add Read to an upcoming meeting.",
      suggestedPreset: getSuggestedPreset(opts.selectedPreset),
    };
  }

  return {
    title: "No Reports",
    subtitle: "Add Read to an upcoming meeting to start seeing reports.",
  };
};

const sortByDate = (report: Report) =>
  (isTopicReport(report) ? report.lastMention : report.startTime)?.toMillis();

const sortByReadScore = (report: Report) =>
  isTopicReport(report) ? null : report.meetingScore ?? 0;

const sortBySource = (report: Report) => {
  if (isTopicReport(report)) {
    if (report.provider === "unknown") return null;
    return toLower(
      PRODUCTIVITY_PROVIDERS_BY_ID[report.provider].displayNameShort
    );
  }
  return report.meetingPlatform;
};

const sortByTitle = (report: Report) => toLower(report.title);

export const ReportSortUtils = {
  date: sortByDate,
  readScore: sortByReadScore,
  source: sortBySource,
  title: sortByTitle,
};

export const generateProcessingTopicReport = (
  user: User | null,
  provider: Topic["provider"]
): Topic | null => {
  if (!user) return null;
  return {
    id: "",
    title: "Your first Readout",
    firstMention: DateTime.now(),
    lastMention: DateTime.now(),
    channelProviders: [],
    contributors: [],
    isHidden: false,
    impersonationCount: 0,
    keywords: [],
    kind: "semantic",
    messageCount: 0,
    channelCount: 0,
    ownerName: user.fullName,
    ownerUserId: user.id,
    provider,
    status: "processing",
    summary: "",
  };
};

export const getReportStartEndTimes = (
  report: UserMeeting,
  isSummary: boolean
) => {
  // use the scheduled start time if available for all meeting types, provided
  // the scheduled start time is before the observed end time (i.e., account for
  // cases where someone joined early, left before the call started, and the bot
  // left thinking the meeting was over before it had technically begun)
  const useScheduledStartTime =
    report.scheduledStartTime != null &&
    (!report.endTime || report.scheduledStartTime < report.endTime);

  const startTimeToDisplay = useScheduledStartTime
    ? report.scheduledStartTime
    : report.startTime;

  // only summary rows should use the scheduled end time, since individual
  // sessions could have ended early or ran long
  const endTimeToDisplay = isSummary ? report.scheduledEndTime : report.endTime;

  return {
    startTimeToDisplay,
    endTimeToDisplay,
  };
};
